import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MotifModule } from "@ey-xd/motif-wc-angular"; 
import {
  SurveyQuestion,
  SurveyQuestionType,
  SurveyTextType,
} from 'app/models/feedback-question';

@Component({
  selector: 'app-feedback-question',
  standalone: true,
  imports: [
    CommonModule,
    MotifModule,
    ReactiveFormsModule
  ],
  templateUrl: './feedback-question.component.html',
  styleUrl: './feedback-question.component.scss',
})
export class FeedbackQuestionComponent implements OnChanges {
  @Input() question?: SurveyQuestion;
  @Input() formGroup?: FormGroup;

  @ViewChildren('optionContainer') optionContainers?: QueryList<ElementRef>;

  FeedbackQuestionType = SurveyQuestionType;
  FeedbackTextType = SurveyTextType;

  private _optionFormGroups?: FormGroup[];
  get optionFormGroups(): FormGroup[] | undefined {
    if (!this.formGroup) {
      return undefined;
    }

    if (!this._optionFormGroups) {
      this._optionFormGroups = (this.formGroup.get('options') as FormArray)
        .controls as FormGroup[];
    }

    return this._optionFormGroups;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['question']) {
      setTimeout(() => {
        this.updateCheckboxesColors();
      }, 1);
    }
  }

  private updateCheckboxesColors(): void {
    if (!this.question?.options) {
      return;
    }

    if (!this.optionContainers) {
      setTimeout(() => {
        this.updateCheckboxesColors();
      }, 10);
      return;
    }

    this.optionContainers.forEach((container, index) => {
      const option = this.question!.options![index];
      if (option) {
        container.nativeElement.style.setProperty(
          '--label-color',
          option.label_color || '#000000'
        );
      }
    });
  }

  onOptionCheckboxChange(
    changedCheckboxIndex: number,
    isChecked: any
  ): void {
    if (!this.optionFormGroups?.length) {
      return;
    }

    const changedOptionFormGroup = this.optionFormGroups[changedCheckboxIndex];
    const changedOptionCheckedControl =
      changedOptionFormGroup?.get('optionChecked');
    const checkedFromEvent = this.parseCheckboxValue(isChecked);
    const checkedFromControl = changedOptionCheckedControl?.value;
    const isOptionChecked =
      typeof checkedFromEvent === 'boolean'
        ? checkedFromEvent
        : !!checkedFromControl;

    if (
      changedOptionCheckedControl &&
      changedOptionCheckedControl.value !== isOptionChecked
    ) {
      changedOptionCheckedControl.setValue(isOptionChecked, {
        emitEvent: false,
      });
    }

    if (!isOptionChecked) {
      this.optionFormGroups[changedCheckboxIndex]
        ?.get('textInput')
        ?.setValue('');
      this.optionFormGroups[changedCheckboxIndex]
        ?.get('textInput')
        ?.disable({ emitEvent: false });
      return;
    }

    this.optionFormGroups[changedCheckboxIndex]
      ?.get('textInput')
      ?.enable({ emitEvent: false });

    this.optionFormGroups.forEach((formGroup, index) => {
      if (index != changedCheckboxIndex) {
        formGroup.get('optionChecked')?.setValue(false, { emitEvent: false });
        formGroup.get('textInput')?.setValue('', { emitEvent: false });
        formGroup.get('textInput')?.disable({ emitEvent: false });
      }
    });
  }

  private parseCheckboxValue(eventValue: any): boolean | undefined {
    if (typeof eventValue === 'boolean') {
      return eventValue;
    }

    if (
      eventValue &&
      typeof eventValue === 'object' &&
      typeof eventValue.checked === 'boolean'
    ) {
      return eventValue.checked;
    }

    if (
      eventValue &&
      typeof eventValue === 'object' &&
      eventValue.detail &&
      typeof eventValue.detail === 'object' &&
      typeof eventValue.detail.checked === 'boolean'
    ) {
      return eventValue.detail.checked;
    }

    if (
      eventValue &&
      typeof eventValue === 'object' &&
      typeof eventValue.detail === 'boolean'
    ) {
      return eventValue.detail;
    }

    if (
      eventValue &&
      typeof eventValue === 'object' &&
      eventValue.target &&
      typeof eventValue.target.checked === 'boolean'
    ) {
      return eventValue.target.checked;
    }

    return undefined;
  }
}
